import React from 'react';
import { useSupportSubjectsListSlice } from '@restore/SupportSubject/SupportSubject.slice';
import { TableNew } from '@dynamika-shared/ui/TableNew/TableNew';
import { TrashIcon } from '@dynamika-shared/ui/icons';
import { createUploadsTableConfig } from './UploadsTableConfig';
import { Props, UploadDetailKind, UploadRow } from './types';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { TicketCommentModal } from '../common/TicketCommentModal';
import { JsonPayloadModal } from '../common/JsonPayloadModal';
import { ENABLE_REMOVE_UPLOAD, uploadFileTypeLabelMap } from './constants';
import { filterByVisitId } from '../common/renderers';

const getUploadProcessLabel = (fileType?: string, uploadType?: string) => {
  const fileTypeValue = fileType?.trim();
  const uploadTypeValue = uploadType?.trim();

  const fileLabel = fileTypeValue
    ? (uploadFileTypeLabelMap[fileTypeValue] ?? fileTypeValue)
    : 'Upload';
  const typeLabel = uploadTypeValue ? uploadTypeValue.toLowerCase() : '';
  const typeSuffix = typeLabel ? ` (${typeLabel})` : '';

  return `${fileLabel} upload${typeSuffix}`;
};

const getRecordValue = (
  source: unknown,
  keys: string[]
): string | undefined => {
  if (!source || typeof source !== 'object') return undefined;

  const record = source as Record<string, unknown>;

  for (const key of keys) {
    const value = record[key];

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (trimmed) return trimmed;
    }
  }

  return undefined;
};

const getDicomAcquisitionDate = (asset: unknown): string | undefined =>
  getRecordValue(asset, [
    'dicomAcquisitionDate',
    'acquisitionDate',
    'assetAcquisitionDate',
    'acquisition_date',
  ]) ??
  getRecordValue((asset as { dicom?: unknown })?.dicom, [
    'dicomAcquisitionDate',
    'acquisitionDate',
    'assetAcquisitionDate',
    'acquisition_date',
  ]);

export const SubjectUploads: React.FC<Props> = props => {
  const { clinicalTrialId, patientId, visitId, uploads, loading, error } =
    props;

  const state = useSupportSubjectsListSlice();
  const { removingUploadById = {} } = state ?? {};

  const { removeSupportSubjectUpload } = useSupportSubjectsListSlice(
    (_state, actions) => actions
  );

  const invalid = !clinicalTrialId || !patientId;
  const key = `${clinicalTrialId ?? ''}:${patientId ?? ''}`;

  const uploadsForScope = React.useMemo(
    () => filterByVisitId(uploads, visitId),
    [uploads, visitId]
  );

  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const toggleGroup = React.useCallback((groupKey: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey);
      return next;
    });
  }, []);

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<{
    uploadId: string;
    key: string;
  } | null>(null);
  const [jsonModalState, setJsonModalState] = React.useState<{
    open: boolean;
    title: string;
    payload?: unknown;
  }>({
    open: false,
    title: '',
  });

  const handleDeleteGroupClick = React.useCallback(
    (row: UploadRow) => {
      if (ENABLE_REMOVE_UPLOAD) {
        if (row.type !== 'group') return;
        if (!clinicalTrialId || !row.uploadId) return;

        setPendingDelete({ uploadId: row.uploadId, key });
        setDeleteModalOpen(true);
      }
    },
    [clinicalTrialId, key]
  );

  const handleDeleteModalConfirm = React.useCallback(
    (data: { ticket?: string; comment?: string }) => {
      if (!pendingDelete || !clinicalTrialId) return;

      const ticket = data.ticket?.trim();
      const comment = data.comment?.trim();

      if (!ticket) return;

      removeSupportSubjectUpload({
        clinicalTrialId,
        key: pendingDelete.key,
        uploadId: pendingDelete.uploadId,
        ticket,
        comment,
      });

      setDeleteModalOpen(false);
      setPendingDelete(null);
    },
    [pendingDelete, clinicalTrialId, removeSupportSubjectUpload]
  );

  const handleDeleteModalCancel = React.useCallback(() => {
    setDeleteModalOpen(false);
    setPendingDelete(null);
  }, []);

  const handleJsonModalClose = React.useCallback(() => {
    setJsonModalState({
      open: false,
      title: '',
      payload: undefined,
    });
  }, []);

  const submitting = React.useMemo(() => {
    if (!pendingDelete?.uploadId) return false;
    return !!removingUploadById[pendingDelete.uploadId];
  }, [pendingDelete, removingUploadById]);

  const baseCfg = createUploadsTableConfig({
    headerBgInline: '#F6F9FF',
    rowBgInline: '#FFFFFF80',
    headerBgClass: 'bg-[#F6F9FF]',
    rowBgClass: 'bg-white',
    onDeleteGroupClick: handleDeleteGroupClick,
    onViewExecutionRequest: row => {
      setJsonModalState({
        open: true,
        title: 'Request JSON',
        payload: row.requestData,
      });
    },
    onViewExecutionResponse: row => {
      setJsonModalState({
        open: true,
        title: 'Response JSON',
        payload: row.responseData,
      });
    },
  });

  const patchedColumns = baseCfg.columns.map(col => {
    if (col.key !== 'actions') return col;
    const originalRender = col.render;
    return {
      ...col,
      render: (value: unknown, row: UploadRow, index: number) => {
        if (row.type !== 'group') {
          return originalRender ? originalRender(value, row, index) : null;
        }
        const isOpen = expanded.has(row.key);
        const isRemoving = !!row.uploadId && !!removingUploadById[row.uploadId];

        return (
          <div className='w-full flex items-center justify-end gap-1 pr-1'>
            <span className='text-[25px] shrink-0'>
              <button
                type='button'
                aria-label='Delete upload group'
                disabled={isRemoving}
                className='text-[25px] shrink-0 inline-flex items-center justify-center disabled:opacity-50'
                onClick={e => {
                  e.stopPropagation();
                  if (!isRemoving) {
                    handleDeleteGroupClick(row);
                  }
                }}
              >
                <TrashIcon />
              </button>
            </span>
            <button
              type='button'
              onClick={e => {
                e.stopPropagation();
                toggleGroup(row.key);
              }}
              aria-expanded={isOpen}
              className='shrink-0 inline-flex items-center justify-center h-3 w-3 rounded-full hover:bg-black/5'
            >
              <ChevronIcon
                dir={isOpen ? 'up' : 'down'}
                className='h-2 w-2 text-spanishGray transition-transform duration-200'
              />
            </button>
          </div>
        );
      },
    };
  });

  const realRows: UploadRow[] = React.useMemo(() => {
    if (!uploadsForScope?.length) {
      return [];
    }

    const sorted = [...uploadsForScope].sort((a, b) => {
      const ad = a.uploadDate ? new Date(a.uploadDate).getTime() : 0;
      const bd = b.uploadDate ? new Date(b.uploadDate).getTime() : 0;
      return bd - ad;
    });

    const perUploadRows = sorted.map((upload, index) => {
      const groupKey = upload.uploadItemId ?? `upload-${index}`;

      const groupRow: UploadRow = {
        type: 'group',
        key: groupKey,
        processLabel: getUploadProcessLabel(upload.fileType, upload.uploadType),
        user: upload.uploadUser?.fullName ?? '',
        userEmail: upload.uploadUser?.email ?? '',
        uploadDate: upload.uploadDate ?? '',
        preprocessingStatus: upload.preprocessingStatus ?? '',
        uploadStatus: upload.uploadStatus ?? '',
        uploadId: upload.uploadItemId ?? '',
      };

      const execHeaderRow: UploadRow = {
        type: 'detail',
        key: `${groupKey}-h-exec`,
        groupKey,
        detailKind: UploadDetailKind.SUBHEADER_EXEC,
      };

      const execs = upload.executions ?? [];
      const execRows: UploadRow[] = execs.map((ex, idx) => {
        const wf = ex.workflowExecution;
        return {
          type: 'detail',
          key: `${groupKey}-exec-${idx}`,
          groupKey,
          detailKind: UploadDetailKind.EXEC,
          execLabel:
            wf?.workflowName ||
            wf?.workflowType ||
            wf?.module ||
            `Execution ${idx + 1}`,
          startDate: wf?.started ?? '',
          endDate: wf?.finished ?? '',
          detailUploadStatus:
            wf?.status ?? ex.uploadWorkflowExecution?.status ?? '',
          requestData: wf?.request,
          responseData: wf?.response,
        };
      });

      const assetsHeaderRow: UploadRow = {
        type: 'detail',
        key: `${groupKey}-h-assets`,
        groupKey,
        detailKind: UploadDetailKind.SUBHEADER_ASSETS,
      };

      const assets = upload.uploadAssets ?? [];
      const assetRows: UploadRow[] = assets.map((asset, idx) => ({
        type: 'detail',
        key: `${groupKey}-asset-${idx}`,
        groupKey,
        detailKind: UploadDetailKind.ASSETS,
        assetLabel: asset.assetType ?? 'Asset',
        dicomSeriesUid: asset.dicom ?? '—',
        dicomAcquisitionDate: getDicomAcquisitionDate(asset),
      }));

      return [
        groupRow,
        execHeaderRow,
        ...execRows,
        assetsHeaderRow,
        ...assetRows,
      ];
    });

    return perUploadRows.flat();
  }, [uploadsForScope]);

  const visibleRows: UploadRow[] = React.useMemo(() => {
    const out: UploadRow[] = [];
    for (const r of realRows) {
      if (r.type === 'detail' && !expanded.has(r.groupKey)) continue;
      out.push(r);
    }
    return out;
  }, [realRows, expanded]);

  const renderContent = () => {
    if (invalid) {
      return null;
    }

    if (loading) {
      return <div>Loading uploads…</div>;
    }

    if (error) {
      return React.createElement(
        'div',
        { className: 'text-red-600' },
        'Failed to load uploads.'
      );
    }

    if (uploadsForScope.length === 0) {
      return <div>No uploads yet.</div>;
    }

    return (
      <>
        <TableNew<UploadRow>
          tableClassName='main-table mt-0'
          rowKey='key'
          columns={patchedColumns}
          dataSource={visibleRows}
          pagination={false}
          components={baseCfg.components}
          rowClassName={baseCfg.rowClassName}
          onRow={baseCfg.onRow}
          style={{ margin: 0 }}
        />
        <TicketCommentModal
          open={deleteModalOpen}
          onCancel={handleDeleteModalCancel}
          onConfirm={handleDeleteModalConfirm}
          submitting={submitting}
          title='Remove upload item'
          messageLines={['Are you sure you want to remove this upload item?']}
          okText='Remove'
          cancelText='Cancel'
        />
        <JsonPayloadModal
          open={jsonModalState.open}
          onCancel={handleJsonModalClose}
          title={jsonModalState.title}
          payload={jsonModalState.payload}
        />
      </>
    );
  };

  return <div>{renderContent()}</div>;
};
